<template>
  <div class="hyrox-score-shell">
    <!-- Back button -->
    <v-btn style="position: fixed; top: 15px; z-index: 99; left: 15px" icon large @click="goBack" class="back-btn">
      <v-icon color="white">mdi-chevron-left</v-icon>
    </v-btn>

    <v-container class="hyrox-score-container py-12 px-4">
      <div v-if="loading" class="text-center mt-12">
        <v-progress-circular indeterminate color="#fbc02d" size="64"></v-progress-circular>
        <div class="grey--text text--lighten-1 mt-4 subtitle-1">Lade Event-Details...</div>
      </div>

      <div v-else-if="isClosed" class="text-center py-12 closed-container">
        <v-icon size="80" color="#f50057" class="mb-4">mdi-calendar-remove</v-icon>
        <h1 class="text-h4 font-weight-black white--text mb-3">ANMELDUNG GESCHLOSSEN</h1>
        <p class="grey--text text--lighten-1 body-1 max-width-text mx-auto">
          Die Anmeldefrist für das HYROX Score Event am 19. Juli ist am 11. Juli abgelaufen. Eine Registrierung ist leider nicht mehr möglich.
        </p>
        <v-btn color="#fbc02d" class="black--text font-weight-bold mt-6 rounded-lg" large @click="goBack">
          Zurück zur Startseite
        </v-btn>
      </div>

      <div v-else-if="isSoldOut" class="text-center py-12 closed-container">
        <v-icon size="80" color="#f50057" class="mb-4">mdi-ticket-percent</v-icon>
        <h1 class="text-h4 font-weight-black white--text mb-3">AUSVERKAUFT</h1>
        <p class="grey--text text--lighten-1 body-1 max-width-text mx-auto">
          Alle Startplätze für das HYROX Score Event am 19. Juli in St. Gallen West sind bereits vergeben.
        </p>
        <div class="warning-box pa-4 rounded-lg my-6 max-width-text mx-auto">
          Melanie wurde benachrichtigt. Eventuell wird das Kontingent in Kürze erweitert. Schau später nochmals vorbei!
        </div>
        <v-btn color="#fbc02d" class="black--text font-weight-bold mt-2 rounded-lg" large @click="goBack">
          Zurück zur Startseite
        </v-btn>
      </div>

      <v-card v-else class="hyrox-score-card rounded-xl elevation-24" flat>
        <div class="card-glow"></div>
        <div class="pa-6 pa-sm-8 relative-content">
          <!-- Event Header -->
          <div class="text-center mb-6">
            <span class="event-tag">19. JULI 2026</span>
            <h1 class="event-headline mt-2 text-uppercase">HYROX Score Event</h1>
            <h2 class="event-subheadline text-uppercase yellow--text text--darken-2">St. Gallen West</h2>
            <div class="divider mx-auto my-3"></div>
          </div>

          <v-window v-model="step" touchless>
            <!-- STEP 1: Info & Form Selector -->
            <v-window-item :value="1">
              <p class="text-center grey--text text--lighten-2 body-2 mb-6" style="max-width: 550px; margin: 0 auto; line-height: 1.6">
                Melde dich alleine oder als 2er Team für das legendäre HYROX Score Event an. Teilnahme ausschliesslich für Well Come Fit Mitglieder.
              </p>

              <!-- Stats bar -->
              <v-row class="mb-6" dense>
                <v-col cols="12" sm="4">
                  <div class="info-metric pa-4 rounded-xl text-center">
                    <v-icon color="#fbc02d" class="mb-1">mdi-clock-outline</v-icon>
                    <div class="caption grey--text">Briefing / Start</div>
                    <div class="font-weight-black white--text subtitle-2">08:45 / 09:00 Uhr</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="info-metric pa-4 rounded-xl text-center">
                    <v-icon color="#fbc02d" class="mb-1">mdi-account-group-outline</v-icon>
                    <div class="caption grey--text">Anmeldung</div>
                    <div class="font-weight-black white--text subtitle-2">Einzeln oder 2er Team</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="info-metric pa-4 rounded-xl text-center">
                    <v-icon color="#fbc02d" class="mb-1">mdi-currency-chf</v-icon>
                    <div class="caption grey--text">Gebühr</div>
                    <div class="font-weight-black white--text subtitle-2">CHF 20 pro Starter</div>
                  </div>
                </v-col>
              </v-row>

              <!-- Registration Mode Toggle -->
              <div class="d-flex justify-center mb-6">
                <v-btn-toggle v-model="registrationMode" mandatory class="type-toggle rounded-xl">
                  <v-btn value="team" class="px-6 py-4 font-weight-black text-subtitle-2">
                    <v-icon left>mdi-account-group</v-icon> Als Team (2er)
                  </v-btn>
                  <v-btn value="single" class="px-6 py-4 font-weight-black text-subtitle-2">
                    <v-icon left>mdi-account</v-icon> Als Einzelstarter
                  </v-btn>
                </v-btn-toggle>
              </div>

              <v-form ref="form" v-model="valid">
                <!-- Participant 1 (Autofilled Logged-in User) -->
                <div class="user-info-box pa-4 rounded-xl mb-6">
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="24" color="#fbc02d" class="mr-2 black--text font-weight-bold">1</v-avatar>
                    <span class="font-weight-black white--text subtitle-2">
                      {{ registrationMode === 'team' ? 'Teilnehmer 1 (Du)' : 'Deine Angaben' }}
                    </span>
                  </div>
                  <v-row dense>
                    <v-col cols="6">
                      <v-text-field
                        :value="user.vorname"
                        label="Vorname"
                        disabled
                        outlined
                        dense
                        dark
                        hide-details
                        class="custom-field"></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        :value="user.nachname"
                        label="Nachname"
                        disabled
                        outlined
                        dense
                        dark
                        hide-details
                        class="custom-field"></v-text-field>
                    </v-col>
                    <v-col cols="12" class="mt-2">
                      <v-text-field
                        :value="user.email"
                        label="E-Mail-Adresse"
                        disabled
                        outlined
                        dense
                        dark
                        hide-details
                        class="custom-field"></v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Participant 2 (Partner Inputs) -->
                <div v-if="registrationMode === 'team'" class="user-info-box pa-4 rounded-xl border-highlight mb-6">
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="24" color="#fbc02d" class="mr-2 black--text font-weight-bold">2</v-avatar>
                    <span class="font-weight-black white--text subtitle-2">Teilnehmer 2 (Partner)</span>
                  </div>
                  
                  <v-alert type="info" outlined dense color="#fbc02d" class="caption mb-4 dark-alert">
                    Die E-Mail-Adresse deines Partners muss zwingend mit dem Well Come Fit Mitgliederkonto übereinstimmen.
                  </v-alert>

                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="partner.firstName"
                        label="Vorname*"
                        required
                        :rules="[v => !!v || 'Vorname ist erforderlich']"
                        outlined
                        dense
                        dark
                        class="custom-field"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="partner.lastName"
                        label="Nachname*"
                        required
                        :rules="[v => !!v || 'Nachname ist erforderlich']"
                        outlined
                        dense
                        dark
                        class="custom-field"></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="partner.email"
                        label="E-Mail-Adresse*"
                        required
                        :rules="[
                          v => !!v || 'E-Mail ist erforderlich',
                          v => /.+@.+\..+/.test(v) || 'Ungültige E-Mail-Adresse'
                        ]"
                        outlined
                        dense
                        dark
                        class="custom-field"></v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Next button -->
                <v-btn
                  color="#fbc02d"
                  block
                  large
                  class="black--text font-weight-black rounded-lg py-6"
                  :disabled="!valid"
                  @click="submitRegistrationCheck">
                  Weiter zur Überprüfung
                </v-btn>
              </v-form>
            </v-window-item>

            <!-- STEP 2: Validation Loader / Price Summary -->
            <v-window-item :value="2">
              <div v-if="verifying" class="text-center py-12">
                <v-progress-circular indeterminate color="#fbc02d" size="50"></v-progress-circular>
                <div class="white--text font-weight-bold mt-4 subtitle-1">Überprüfe Mitgliedschaften & Berechtigungen...</div>
                <div class="grey--text caption mt-2">Wir gleichen die Profile mit dem System ab.</div>
              </div>

              <div v-else-if="verificationError" class="text-center py-6">
                <v-icon size="64" color="red" class="mb-4">mdi-alert-circle-outline</v-icon>
                <h3 class="text-h6 font-weight-bold white--text mb-2">Prüfung fehlgeschlagen</h3>
                <p class="red--text text--lighten-2 body-2 px-4 max-width-text mx-auto" v-html="verificationError"></p>
                <v-btn text color="grey" class="mt-4" @click="step = 1">
                  Zurück zur Eingabe
                </v-btn>
              </div>

              <div v-else>
                <h3 class="subtitle-1 font-weight-black white--text mb-4 text-center">Registrierungs-Zusammenfassung</h3>

                <div class="summary-box pa-4 rounded-xl mb-6">
                  <!-- User -->
                  <div class="d-flex justify-space-between align-center py-2" :class="{ 'border-bottom': registrationMode === 'team' }">
                    <span class="grey--text">{{ registrationMode === 'team' ? 'Teilnehmer 1 (Du):' : 'Anmelder (Du):' }}</span>
                    <span class="white--text font-weight-bold">
                      {{ user.vorname }} {{ user.nachname }} 
                      <v-chip x-small color="green" class="ml-1 black--text font-weight-black" v-if="userIsInstructor">KURSLEITER</v-chip>
                      <v-chip x-small outlined color="grey" class="ml-1" v-else>MITGLIED</v-chip>
                    </span>
                  </div>

                  <!-- Partner (Only for Team Mode) -->
                  <div v-if="registrationMode === 'team'" class="d-flex justify-space-between align-center py-2">
                    <span class="grey--text">Teilnehmer 2 (Partner):</span>
                    <span class="white--text font-weight-bold">
                      {{ partner.firstName }} {{ partner.lastName }}
                      <v-chip x-small color="green" class="ml-1 black--text font-weight-black" v-if="partnerIsInstructor">KURSLEITER</v-chip>
                      <v-chip x-small outlined color="grey" class="ml-1" v-else>MITGLIED</v-chip>
                    </span>
                  </div>

                  <v-divider class="grey darken-3 my-2"></v-divider>

                  <div class="d-flex justify-space-between align-center py-2 pt-2">
                    <span class="white--text font-weight-bold">Gesamtbetrag:</span>
                    <span class="yellow--text text--darken-2 font-weight-black text-h6">
                      CHF {{ totalFee.toFixed(2) }}
                    </span>
                  </div>
                </div>

                <div class="d-flex flex-column flex-sm-row justify-space-between gap-4 mt-6">
                  <v-btn text color="grey" large class="rounded-lg" @click="step = 1">
                    Zurück
                  </v-btn>
                  <v-btn
                    color="#fbc02d"
                    class="black--text font-weight-bold rounded-lg px-6"
                    large
                    :loading="registering"
                    @click="proceedToPayment">
                    {{ totalFee > 0 ? "Weiter zur Zahlung" : "Jetzt kostenlos anmelden" }}
                  </v-btn>
                </div>
              </div>
            </v-window-item>

            <!-- STEP 3: Simplified Payment Open Workflow -->
            <v-window-item :value="3">
              <h3 class="subtitle-1 font-weight-black white--text mb-4 text-center">Zahlung</h3>

              <div class="payment-box text-center pa-6 rounded-xl mb-6">
                <v-icon color="#fbc02d" size="48" class="mb-2">mdi-wallet-membership</v-icon>
                <div class="white--text font-weight-black subtitle-1 mb-2">Gesamtkosten für deine Anmeldung</div>
                <div class="yellow--text text--darken-2 font-weight-black text-h4 mb-4">CHF {{ totalFee.toFixed(2) }}</div>

                <v-divider class="grey darken-3 my-4"></v-divider>

                <p class="caption grey--text text--lighten-2 text-left" style="line-height: 1.5">
                  <strong>Hinweis zur Zahlung:</strong><br>
                  Der finale Zahlungsprozess wird in Kürze direkt in dieser App freigeschaltet. Klicke auf den Button unten, um deine Buchung zu sichern. Deine Anmeldung ist verbindlich. Die Zahlung wird anschliessend vor dem Event-Tag abgewickelt.
                </p>
              </div>

              <div class="d-flex flex-column flex-sm-row justify-space-between gap-4">
                <v-btn text color="grey" large class="rounded-lg" @click="step = 2">
                  Zurück
                </v-btn>
                <v-btn
                  color="#fbc02d"
                  class="black--text font-weight-bold rounded-lg px-6"
                  large
                  :loading="registering"
                  @click="executeRegistration">
                  Jetzt zahlungspflichtig anmelden
                </v-btn>
              </div>
            </v-window-item>

            <!-- STEP 4: Success Screen with 5k Run Promos -->
            <v-window-item :value="4">
              <div class="text-center py-6">
                <v-icon size="80" color="green" class="mb-4 animate-bounce">mdi-check-circle-outline</v-icon>
                <h2 class="text-h5 font-weight-black white--text mb-2">ANMELDUNG ERFOLGREICH!</h2>
                <p class="grey--text text--lighten-1 body-2 max-width-text mx-auto mb-6">
                  Vielen Dank für deine Anmeldung zum HYROX Score Event. Wir haben eine Anmeldebestätigung an dich {{ registrationMode === 'team' ? 'und deinen Partner' : '' }} per E-Mail gesendet.
                </p>

                <v-card color="rgba(255, 255, 255, 0.03)" class="rounded-xl border-highlight pa-5 text-left mb-6 mx-auto" style="max-width: 500px">
                  <h3 class="subtitle-2 font-weight-black yellow--text text--darken-2 text-uppercase mb-3 d-flex align-center">
                    <v-icon color="#fbc02d" small class="mr-2">mdi-run</v-icon>
                    Du willst deinen 5k Run ebenfalls mit uns machen?
                  </h3>
                  <p class="caption grey--text mb-4">
                    Melde dich direkt zu den passenden Gruppenkursen an:
                  </p>
                  
                  <div class="d-flex flex-column gap-3">
                    <v-btn
                      href="https://wellcomefit.ch/gruppenkurs-frauenfeld"
                      target="_blank"
                      block
                      outlined
                      color="#fbc02d"
                      class="text-none font-weight-bold py-5 rounded-lg">
                      5k Run am 18. Juli in Frauenfeld
                    </v-btn>
                    <v-btn
                      href="https://wellcomefit.ch/gruppenkurs-stgallen"
                      target="_blank"
                      block
                      outlined
                      color="#fbc02d"
                      class="text-none font-weight-bold py-5 rounded-lg">
                      5k Run am 18. Juli in St. Gallen
                    </v-btn>
                  </div>
                </v-card>

                <div class="info-alert pa-4 rounded-xl max-width-text mx-auto text-left mb-6">
                  <v-icon small color="info" class="mr-2">mdi-information-outline</v-icon>
                  <span class="caption grey--text text--lighten-1">
                    Weitere Informationen sowie die Zugänge zur HYROX-App und zur Capture-App erhältst du nach Anmeldeschluss per E-Mail.
                  </span>
                </div>

                <v-btn color="#fbc02d" class="black--text font-weight-black rounded-lg px-8" large @click="goHome">
                  Fertigstellen
                </v-btn>
              </div>
            </v-window-item>
          </v-window>
        </div>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import api from "@/api"
import { mapState } from "vuex"

export default {
  name: "HyroxScoreRegistration",
  data() {
    return {
      step: 1,
      loading: true,
      valid: false,
      verifying: false,
      registering: false,
      isClosed: false,
      isSoldOut: false,
      verificationError: null,
      
      // Mode Selection: 'team' or 'single'
      registrationMode: "team",

      // Form Inputs
      partner: {
        firstName: "",
        lastName: "",
        email: ""
      },

      // Computed backend check properties
      userIsInstructor: false,
      partnerIsInstructor: false,
      totalFee: 40,
      registrationId: null
    }
  },
  computed: {
    ...mapState("userStore", ["user"])
  },
  async created() {
    await this.fetchEventAvailability()
  },
  methods: {
    goBack() {
      this.$router.go(-1)
    },
    goHome() {
      this.$router.push("/home")
    },
    async fetchEventAvailability() {
      try {
        const stats = await api.hyroxScore.fetchAvailability()
        this.isClosed = stats.isClosed
        this.isSoldOut = stats.activeParticipants >= stats.limit
        
        // If user already signed up, route them directly to confirmation if active
        if (stats.myRegistration) {
          this.step = 4
          if (stats.myRegistration.partner && stats.myRegistration.partner.email) {
            this.registrationMode = "team"
          } else {
            this.registrationMode = "single"
          }
        }
      } catch (e) {
        console.error("Failed to load hyrox score stats", e)
      } finally {
        this.loading = false
      }
    },
    async submitRegistrationCheck() {
      if (!this.$refs.form.validate()) return

      this.step = 2
      this.verifying = true
      this.verificationError = null

      try {
        const payload = {
          partnerFirstName: "",
          partnerLastName: "",
          partnerEmail: ""
        }

        if (this.registrationMode === "team") {
          payload.partnerFirstName = this.partner.firstName
          payload.partnerLastName = this.partner.lastName
          payload.partnerEmail = this.partner.email
        }
        
        const res = await api.hyroxScore.register(payload)
        this.registrationId = res.registrationId
        this.totalFee = res.totalFee
        
        // Set tag highlights depending on registration fee
        if (this.totalFee === 0) {
          this.userIsInstructor = true
          this.partnerIsInstructor = true
        } else if (this.totalFee === 20) {
          // If team registration and fee is 20, one of them is an instructor.
          if (this.registrationMode === "team") {
            // Placeholder: Backend does verification, so we'll just display summary nicely
            this.userIsInstructor = false
            this.partnerIsInstructor = false
          } else {
            this.userIsInstructor = false
          }
        }
        
        this.verifying = false
        if (this.totalFee === 0) {
          this.step = 4 // Direct success
        } else {
          this.step = 3 // Go to payment
        }
      } catch (error) {
        this.verifying = false
        const msg = error.response?.data || "Überprüfung fehlgeschlagen. Bitte vergewissere dich, dass eine aktive Mitgliedschaft besteht."
        this.verificationError = msg
      }
    },
    proceedToPayment() {
      if (this.totalFee > 0) {
        this.step = 3
      } else {
        this.step = 4
      }
    },
    async proceedToSuccess() {
      this.step = 4
    },
    async executeRegistration() {
      if (!this.registrationId) return
      this.registering = true
      
      try {
        await api.hyroxScore.confirmPayment(this.registrationId)
        this.step = 4 // Show success page
      } catch (error) {
        this.$toast.error(error.response?.data || "Zahlungsbestätigung fehlgeschlagen.")
      } finally {
        this.registering = false
      }
    }
  }
}
</script>

<style scoped>
.hyrox-score-shell {
  background-color: #0f0f0f;
  min-height: 100vh;
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.back-btn {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.hyrox-score-container {
  max-width: 650px;
  margin: 0 auto;
}

.closed-container {
  background-color: #1c1c1e;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  padding: 40px 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

.max-width-text {
  max-width: 480px;
}

.warning-box {
  background-color: rgba(245, 0, 87, 0.08);
  border: 1px solid rgba(245, 0, 87, 0.2);
  color: #f50057;
  font-weight: 500;
  font-size: 0.9rem;
}

/* Card Styling */
.hyrox-score-card {
  position: relative;
  background-color: #1c1c1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  overflow: hidden;
}

.card-glow {
  position: absolute;
  top: -150px;
  left: 50%;
  transform: translateX(-50%);
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(251, 192, 45, 0.15) 0%, transparent 70%);
  filter: blur(40px);
  pointer-events: none;
}

.relative-content {
  position: relative;
  z-index: 2;
}

/* Type Toggle */
.type-toggle {
  background-color: rgba(255, 255, 255, 0.04) !important;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 4px;
}

.type-toggle .v-btn {
  color: #888888 !important;
  background-color: transparent !important;
  border-radius: 12px !important;
  border: none !important;
  text-transform: none;
}

.type-toggle .v-btn--active {
  color: #000000 !important;
  background-color: #fbc02d !important;
}

/* Tags & Headlines */
.event-tag {
  display: inline-block;
  background-color: rgba(251, 192, 45, 0.1);
  color: #fbc02d;
  font-weight: 900;
  font-size: 11px;
  letter-spacing: 2px;
  padding: 4px 12px;
  border-radius: 30px;
  border: 1px solid rgba(251, 192, 45, 0.2);
}

.event-headline {
  font-size: clamp(1.8rem, 4vw, 2.4rem);
  font-weight: 950;
  font-style: italic;
  letter-spacing: -1px;
  line-height: 1.1;
  color: #ffffff;
}

.event-subheadline {
  font-size: 1.1rem;
  font-weight: 800;
  letter-spacing: 3px;
  opacity: 0.9;
}

.divider {
  width: 60px;
  height: 3px;
  background-color: #fbc02d;
  border-radius: 2px;
}

/* Metrics & Boxes */
.info-metric {
  background-color: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.user-info-box {
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.border-highlight {
  border: 1px solid rgba(251, 192, 45, 0.2) !important;
}

.dark-alert {
  background-color: rgba(251, 192, 45, 0.05) !important;
  color: #fbc02d !important;
}

.custom-field :deep(.v-input__control) {
  border-radius: 8px !important;
}

.summary-box {
  background-color: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.border-bottom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.payment-box {
  background: linear-gradient(135deg, rgba(251, 192, 45, 0.05) 0%, rgba(0,0,0,0.3) 100%);
  border: 1px solid rgba(251, 192, 45, 0.1);
}

.info-alert {
  background-color: rgba(29, 155, 240, 0.05);
  border: 1px solid rgba(29, 155, 240, 0.15);
}

.gap-3 {
  gap: 12px;
}
.gap-4 {
  gap: 16px;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
.animate-bounce {
  animation: bounce 2s infinite ease-in-out;
}
</style>
